import LibraryAddButton from '@/components/Buttons/LibraryAddButton';
import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import { studioDomain } from '@/environment';
import { useNavigateToAgentsEditor } from '@/hooks/navigation/useNavigateToAgentsEditor';
import ArrowLeft from '@/images/arrows/ArrowLeft.svg';
import LogoDID from '@/images/did_logo_dark.svg';
import { useAnalytics } from '@/services/analytics';
import { AppRoutes } from '@/utils/types';
import { IAgent } from '@d-id/types';
import styled from '@emotion/styled';
import { IconButton } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { AgentEditButton } from './AgentEditButton';
import { AgentEmbedButton } from './AgentEmbedButton';
import { AgentShareButton } from './AgentShareButton';
import { ViewAsGuestButton } from './ViewAsGuestButton';

const maxWidth = '768px';

const DidHeader = styled.header`
    @media (max-width: ${maxWidth}) {
        display: none;
    }

    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-bottom: 24px;
`;

const LeftButtonsContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
`;

const RightButtonsContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
`;

const LogoContainer = styled.img`
    width: 42px;
    &:hover {
        cursor: pointer;
    }
`;

const StyledBackButton = styled(IconButton)`
    padding: 0;
    border-radius: 12px;
    height: 40px;

    &:active {
        background-color: ${({ theme }) => theme.colors.black[50]};
    }

    &:hover {
        background-color: ${({ theme }) => theme.colors.black[100]};
    }
`;

const StyledMaskedIcon = styled(MaskedIcon)`
    color: ${({ theme }) => theme.colors.black[700]};
`;

const BackText = styled.p`
    color: ${({ theme }) => theme.colors.black[700]};
    font-size: 16px;
    font-weight: 500;
    line-height: 160%;
    letter-spacing: 0.15px;
    margin-left: 10px;
    margin-right: 12px;
`;

interface LocationState {
    isOwner?: boolean;
    agent?: IAgent;
    clientKey?: string;
}

interface Props {
    onEdit: (agent?: IAgent) => void;
}

export const AgentShareHeader = ({ onEdit }: Props) => {
    const mixpanel = useAnalytics();
    const navigate = useNavigate();
    const { navigateToAgentEditor } = useNavigateToAgentsEditor();

    const searchParams = new URLSearchParams(useLocation().search);
    const agentIdParam = searchParams.get('id');
    const keyParam = window.atob(searchParams.get('key') ?? '');

    const locationState = useLocation() as { state?: LocationState };
    const { isOwner: isOwnerFromState, agent: agentFromState, clientKey: clientKey } = locationState.state || {};
    const isOwner = isOwnerFromState !== undefined ? isOwnerFromState : false;
    const agentId = agentFromState?.id ?? agentIdParam;
    const key = clientKey ?? keyParam;

    const handleClickBack = () => {
        navigate(`${AppRoutes.Agent}`);
    };

    const handleViewAsGuest = () => {
        window.open(`${studioDomain}${AppRoutes.AgentShare}?id=${agentId}&key=${btoa(key || '')}`);
    };

    if (isOwner) {
        return (
            <DidHeader>
                <LeftButtonsContainer>
                    <StyledBackButton onClick={handleClickBack}>
                        <StyledMaskedIcon src={ArrowLeft} size="32px" />
                        <BackText>Agents</BackText>
                    </StyledBackButton>

                    <ViewAsGuestButton onClick={handleViewAsGuest} white />
                </LeftButtonsContainer>

                <RightButtonsContainer>
                    <AgentEditButton onClick={() => onEdit(agentFromState)} white />
                    <AgentShareButton clientKey={key} agentId={agentId} white />
                    <AgentEmbedButton agent={agentFromState} />
                </RightButtonsContainer>
            </DidHeader>
        );
    }

    return (
        <DidHeader>
            <LogoContainer alt="DID Logo" src={LogoDID} onClick={() => (location.href = location.origin)} />
            <RightButtonsContainer>
                <AgentShareButton clientKey={key} agentId={agentId} white />
                <LibraryAddButton
                    onClick={() => {
                        navigateToAgentEditor({ mixPanelFrom: 'agent-share-header' });
                        mixpanel?.track('agent-create', { event: 'click', from: 'share-page', agentId: agentId });
                    }}
                    aria-label={'create agent'}>
                    Create agent
                </LibraryAddButton>
            </RightButtonsContainer>
        </DidHeader>
    );
};
